import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import GridOverlay from '../components/GridOverlay';
import Brand from '../components/Brand';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
    const { user, login, isLoading } = useAuth();
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const sessionExpired = params.get('session') === 'expired';
    const next = params.get('next') || null;

    // already-authed → bounce to admin (staff-only sign-in)
    useEffect(() => {
        if (!isLoading && user) {
            navigate(next || '/admin', { replace: true });
        }
    }, [isLoading, user, next, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            const { mustChangePassword } = await login(email, password);
            if (mustChangePassword) {
                navigate('/admin/change-password', { replace: true });
            } else {
                navigate(next || '/admin', { replace: true });
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                setError('Too many failed attempts. Please wait 15 minutes and try again.');
            } else {
                setError('Invalid email or password.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <SEO title="Staff Sign-in - Centurion Matting" description="Centurion Matting staff sign-in. Manage site content and review incoming leads." path="/login" noindex />
            <section
                className="relative flex min-h-[80vh] items-center justify-center bg-brand-base px-6 py-16"
                data-testid="login-page"
            >
                <GridOverlay size={56} opacity={0.16} />
                <div
                    className="relative z-10 w-full max-w-md border border-brand-hairline bg-brand-surface p-8 md:p-10"
                    data-testid="login-card"
                >
                    <div className="flex justify-center">
                        <Brand height={80} interactive={false} asLink={false} testId="login-brand" />
                    </div>

                    <span className="mt-8 block text-center font-display text-[10px] font-bold uppercase tracking-[0.24em] text-brand-gold">
                        Staff Access
                    </span>
                    <h1 className="mt-3 text-center font-display text-2xl font-bold uppercase tracking-tight text-brand-text">
                        Staff Sign-in
                    </h1>
                    <p className="mt-3 text-center font-sans text-sm text-brand-muted">
                        Sign in to manage site content and review incoming leads.
                    </p>

                    {sessionExpired ? (
                        <div
                            data-testid="login-session-expired"
                            className="mt-6 flex items-start gap-3 border border-brand-hairline bg-brand-elevated px-4 py-3"
                        >
                            <AlertCircle className="mt-0.5 flex-none text-brand-gold" size={16} />
                            <p className="font-sans text-xs text-brand-muted">
                                Your session expired. Please sign in again.
                            </p>
                        </div>
                    ) : null}

                    <form onSubmit={onSubmit} className="mt-8 grid gap-5" data-testid="login-form" noValidate>
                        <label className="block">
                            <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
                                Email
                            </span>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="username"
                                data-testid="login-email"
                                className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
                            />
                        </label>
                        <label className="block">
                            <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
                                Password
                            </span>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                data-testid="login-password"
                                className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
                            />
                        </label>

                        {error ? (
                            <p className="font-sans text-sm text-brand-danger" data-testid="login-error">
                                {error}
                            </p>
                        ) : null}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="login-submit"
                            className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-gold px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-[#E5B647] disabled:opacity-60"
                        >
                            {submitting ? (
                                <><Loader2 className="animate-spin" size={14} /> Signing in…</>
                            ) : (
                                'Sign In'
                            )}
                        </button>
                    </form>

                    <p className="mt-8 text-center font-sans text-xs text-brand-muted">
                        Staff access only. Contact your site administrator for credentials.
                    </p>
                    <p className="mt-3 text-center">
                        <Link
                            to="/"
                            className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted hover:text-brand-text"
                        >
                            ← Back to centurionmatting.com
                        </Link>
                    </p>
                </div>
            </section>
        </>
    );
}
